// Staff control for multi-store chains: WHO gets the money mail, and WHO can order for every store.
//
// Two separate switches on purpose. The billing email receives every invoice, payment link, reminder
// and statement for the account (so an 8-store group stops getting 8 separate invoices at 8 store
// addresses), while "orders all stores" is about a head-office buyer placing orders on any store's
// behalf. Often different people — an accounts-payable inbox never places an order.
//
// The billing recipient is a free-text ADDRESS, not a login: payables@… has no platform account and
// shouldn't need one. Picking an existing login from the list below just fills the same field.
import { useCallback, useEffect, useState } from 'react';
import { fetchCompanyContacts, setBillingEmail, setContactAllLocations } from '../api/companies.js';

export default function AccountContactsCard({ companyId, billingEmail, billingName, readOnly, onChanged }) {
  const [contacts, setContacts] = useState(null);
  const [email, setEmail] = useState(billingEmail || '');
  const [name, setName] = useState(billingName || '');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(() => {
    fetchCompanyContacts(companyId)
      .then(setContacts)
      .catch(() => setContacts([]));
  }, [companyId]);

  useEffect(load, [load]);
  useEffect(() => {
    setEmail(billingEmail || '');
    setName(billingName || '');
  }, [billingEmail, billingName]);

  // Only worth showing for a chain — a single-store client has nothing to consolidate.
  const locationCount = new Set((contacts || []).map((c) => c.locationId)).size;
  if (contacts && contacts.length <= 1 && locationCount <= 1) return null;

  const saved = (billingEmail || '') === email.trim().toLowerCase() && (billingName || '') === name.trim();

  const save = async (nextEmail, nextName) => {
    setBusy('billing');
    setError('');
    setNotice('');
    try {
      await setBillingEmail(companyId, nextEmail.trim() ? nextEmail.trim() : null, nextName.trim() || undefined);
      setNotice(
        nextEmail.trim()
          ? `All invoices, payment links and reminders now go to ${nextEmail.trim().toLowerCase()} only.`
          : 'Back to per-store billing.'
      );
      onChanged?.();
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Could not save the billing email.');
    } finally {
      setBusy(null);
    }
  };

  const toggleAllLocations = async (contact) => {
    setBusy(contact.id);
    setError('');
    setNotice('');
    try {
      await setContactAllLocations(contact.id, !contact.allLocations);
      load();
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Could not update that login.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-text-primary">Account billing</h3>
      <p className="mt-1 text-xs text-text-muted">
        This client has {locationCount} store{locationCount === 1 ? '' : 's'}. Send all billing to one address, and choose who
        can order across them.
      </p>

      <label className="mt-3 block text-xs font-medium text-text-secondary" htmlFor="billing-email">
        Billing email
      </label>
      <input
        id="billing-email"
        type="email"
        value={email}
        placeholder="payables@example.com"
        disabled={readOnly || busy === 'billing'}
        onChange={(e) => setEmail(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && !saved && save(email, name)}
        className="mt-1 w-full rounded-md border border-line bg-bg px-2 py-1.5 text-sm text-text-primary disabled:opacity-60"
      />
      <input
        aria-label="Billing contact name (optional)"
        type="text"
        value={name}
        placeholder="Name for the greeting (optional)"
        disabled={readOnly || busy === 'billing'}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && !saved && save(email, name)}
        className="mt-1.5 w-full rounded-md border border-line bg-bg px-2 py-1.5 text-sm text-text-primary disabled:opacity-60"
      />
      {!readOnly && (
        <div className="mt-2 flex items-center gap-2">
          <button
            onClick={() => save(email, name)}
            disabled={busy === 'billing' || saved}
            className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {busy === 'billing' ? 'Saving…' : saved ? 'Saved' : 'Save billing email'}
          </button>
          {billingEmail && (
            <button
              onClick={() => {
                setEmail('');
                setName('');
                save('', '');
              }}
              disabled={busy === 'billing'}
              className="text-xs text-text-muted hover:text-text-primary hover:underline"
            >
              clear
            </button>
          )}
        </div>
      )}
      <p className="mt-1.5 text-xs text-text-muted">
        {billingEmail
          ? 'Every invoice, payment link, reminder and statement for all stores goes here. Store managers are not copied.'
          : "Empty = each order's invoice goes to that store's own contact."}
      </p>
      {/* Shortcut: fill the field from a login already on the account. */}
      {!readOnly && contacts?.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-text-muted">use a login:</span>
          {contacts.map((ct) => (
            <button
              key={ct.id}
              onClick={() => {
                setEmail(ct.email);
                setName(ct.name || '');
              }}
              className="rounded border border-line px-1.5 py-0.5 text-[11px] text-text-secondary hover:border-accent hover:text-accent"
            >
              {ct.email}
            </button>
          ))}
        </div>
      )}

      <div className="mt-4 border-t border-line pt-3">
        <div className="text-xs font-medium text-text-secondary">Can order for every store</div>
        {contacts === null ? (
          <p className="mt-2 text-sm text-text-muted">Loading…</p>
        ) : contacts.length === 0 ? (
          <p className="mt-2 text-sm text-text-muted">No logins on this account yet.</p>
        ) : (
          <ul className="mt-2 space-y-1.5">
            {contacts.map((ct) => (
              <li key={ct.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="min-w-0 truncate text-text-secondary" title={`${ct.email} · ${ct.locationName}`}>
                  {ct.email}
                  <span className="ml-1 text-xs text-text-muted">{ct.locationName}</span>
                </span>
                <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-xs text-text-muted">
                  <input
                    type="checkbox"
                    checked={ct.allLocations}
                    disabled={readOnly || busy === ct.id}
                    onChange={() => toggleAllLocations(ct)}
                    className="h-3.5 w-3.5 rounded border-line"
                  />
                  all stores
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>

      {notice && <p className="mt-3 text-xs text-emerald-600">{notice}</p>}
      {error && <p className="mt-3 text-xs text-danger">{error}</p>}
    </div>
  );
}
